/**
 * What the gift is restricted to, asked — or answered — before the amount (DEC-043,
 * `docs/plans/org-funds.md` §9).
 *
 * ## The word that is not here
 *
 * There is no collective noun on this surface — no *fund*, no *designation*, no *category*. The
 * heading is a question and the options are the organization's own names, verbatim. That is not a
 * copy preference: `packages/domain/src/giving-lane.ts` spends the word *fund* on Lane A, DEC-040
 * made the Foundation an actual §4966 sponsor, and a Give page that also called a mosque's Zakat
 * designation a "fund" would put two different things called a fund in front of the same donor —
 * the exact distinction DEC-028 Q3 rests on, made invisible. *your fund*, *fund balance*,
 * *available to grant*, *advise* and *recommend a grant* are reserved for Lane A and forbidden
 * here; `Checkout.designations.test.tsx` pins it.
 *
 * ## Three states, and the rule that separates them
 *
 * Silence and a picker are not the only two answers, and the difference between the two silent
 * cases is the whole design:
 *
 *  - **One designation** — nothing renders. Not a disabled select, not a single radio, not a line
 *    saying "General". The absence of a choice here carries no information the donor needs: there
 *    was never a decision, so there is nothing to report. A control offering one choice is a
 *    question with no answer, and on a donation flow it reads as a step somebody forgot to remove.
 *  - **Pinned or locked** — a quiet line naming what the gift supports. The absence here would
 *    hide a decision that was *already made for the donor*: somebody who scans the code taped to
 *    the Zakat box is giving to Zakat, and a screen that says nothing leaves them paying without
 *    being able to see what they are paying toward. Pinned adds a **Change**; locked does not,
 *    because the event fixes it and a control that cannot change anything is a lie.
 *  - **Two or more, nothing pinned, not locked** — the picker.
 *
 * A pinned or locked id that is not in `designations` — archived since the code was printed, most
 * likely — renders nothing rather than a blank name, and the server resolves it.
 *
 * The single-element array is the *signal*, and the client does not compute it. The server decides
 * which designations are in the array and it resolves the default when the donor chooses nothing;
 * this file never picks one, never falls back to `isGeneral`, and never sends an id the context
 * did not offer.
 *
 * ## Why native radios rather than the `aria-pressed` buttons used for amounts
 *
 * A designation is a single-answer question, so a radio group is what it is: one tab stop, arrow
 * keys between the options, and a `checked` state that survives forced-colours mode where a
 * `box-shadow` and a border *colour* do not (`apps/theme-contrast.test.ts`, "a selected state").
 * The input is visually hidden and a bound label fills the card, so the whole card is the target.
 *
 * ## Tenant text
 *
 * Every name and description is written by the organization and may be Arabic, Hebrew or Urdu, so
 * each gets `dir="auto"` — the same treatment, and the same reasoning, as `tenant-direction.test.tsx`
 * documents for the header. In the fixed line the name is isolated inside the sentence rather than
 * the sentence being marked as a whole: "Toward" is a strong left-to-right run and would decide the
 * paragraph's direction for a name that is not. The attribute is unconditional: deciding a tenant's
 * script in our code is precisely the inference `CLAUDE.md` forbids.
 */
import { useState } from "react";
import type { ContextResponse, Designation } from "@givehere/contracts";

/**
 * Beyond this many, the cards stop being a glance and start being a page. The amount has to stay
 * reachable without scrolling (`docs/plans/org-funds.md` §9.2), so a longer list collapses to the
 * one-line control the ticket page already uses.
 */
const MAX_CARDS = 4;

/** The question, verbatim (`docs/plans/org-funds.md` §9.4). Not a label, and not a noun. */
export const DESIGNATION_QUESTION = "What would you like to support?";

/** One picker per screen, so one id is enough to bind the long form's label to its control. */
const SELECT_ID = "designation-choice";

/**
 * What this context asks the donor, if anything.
 *
 * Exported because `Checkout` needs the answer twice, for two different reasons, and two copies of
 * this condition would be one copy too many: it decides what is drawn, and — through
 * `sendableDesignationId` — it decides what may travel on a quote.
 */
export type DesignationChoice =
  | { readonly kind: "none" }
  | { readonly kind: "picker"; readonly designations: readonly Designation[] }
  /**
   * Already decided, by a printed code or by the event. `changeable` is what a **Change** control
   * would reveal, and it is empty exactly when there is nothing to reveal — a locked event, or an
   * organization with a single designation, where a picker would break the two-or-more rule.
   */
  | {
      readonly kind: "fixed";
      readonly designation: Designation;
      readonly changeable: readonly Designation[];
    };

const NOTHING: DesignationChoice = { kind: "none" };

export function designationChoice(context: ContextResponse): DesignationChoice {
  const decided =
    context.designationId === null
      ? null
      : (context.designations.find((option) => option.id === context.designationId) ?? null);

  // The event fixes it: a donor-supplied value would be refused rather than honoured, so the donor
  // is told and given nothing to press.
  if (context.designationLocked) {
    return decided ? { kind: "fixed", designation: decided, changeable: [] } : NOTHING;
  }

  // The organization printed a code that already says which one (`packages/signing`, the `d`
  // claim). Overriding that deliberate act takes an act of its own — but the donor can see it.
  if (context.designationId !== null) {
    if (!decided) return NOTHING;
    return {
      kind: "fixed",
      designation: decided,
      // Two or more, still: revealing a picker with one option would break the rule above by the
      // back door.
      changeable: context.designations.length >= 2 ? context.designations : [],
    };
  }

  if (context.designations.length < 2) return NOTHING;
  return { kind: "picker", designations: context.designations };
}

/**
 * The id a quote request may carry — which is not the same as the id the screen is showing.
 *
 * Three separate refusals, each of which has to be true independently:
 *
 *  - **Only what was offered.** A context that reloads into a locked event, or that stops
 *    publishing a designation, cannot leave a stale id attached to the next quote.
 *  - **Never the pinned one, echoed back.** The server refuses an id that conflicts with a pinned
 *    context (`invalid_request`, 400), and null already resolves to exactly that designation. So a
 *    donor who presses Change, looks, and picks the same thing sends nothing — which is the truth.
 *  - **Never a default.** Null means *the donor did not choose*, and the server decides what that
 *    means. It never means "General".
 */
export function sendableDesignationId(
  context: ContextResponse,
  chosen: string | null,
): string | null {
  if (chosen === null) return null;
  if (chosen === context.designationId) return null;
  const choice = designationChoice(context);
  const offered =
    choice.kind === "picker"
      ? choice.designations
      : choice.kind === "fixed"
        ? choice.changeable
        : [];
  return offered.some((option) => option.id === chosen) ? chosen : null;
}

export function DesignationPicker({
  choice,
  selectedId,
  onChoose,
}: {
  choice: DesignationChoice;
  /** Null until the donor answers; the server resolves the default and this never guesses it. */
  selectedId: string | null;
  onChoose: (designationId: string) => void;
}) {
  /**
   * Whether the donor asked to see the alternatives to a pinned designation.
   *
   * Local, because it is a fact about this donor's attention and not about the gift: nothing
   * downstream — no quote, no payment, no receipt — changes because a list was looked at.
   */
  const [revealed, setRevealed] = useState(false);

  if (choice.kind === "none") return null;

  // The quiet line. Also the fallback if `revealed` somehow survives into a state with nothing to
  // reveal, so a locked event can never be talked into drawing a control.
  if (choice.kind === "fixed" && !(revealed && choice.changeable.length > 0)) {
    return (
      <p className="designation-fixed">
        <span className="designation-fixed__text">
          Toward{" "}
          <span className="designation-fixed__name" dir="auto">
            {choice.designation.name}
          </span>
        </span>
        {choice.changeable.length > 0 && (
          <button type="button" className="link" onClick={() => setRevealed(true)}>
            Change
          </button>
        )}
      </p>
    );
  }

  const designations = choice.kind === "picker" ? choice.designations : choice.changeable;
  /**
   * What is shown as chosen, which is deliberately not what is sent.
   *
   * A donor who presses Change is looking at a decision already in force, and a list with nothing
   * marked would tell them the opposite. So the pinned designation reads as the current answer
   * while `sendableDesignationId` keeps it off the wire.
   */
  const shown = selectedId ?? (choice.kind === "fixed" ? choice.designation.id : null);

  if (designations.length > MAX_CARDS) {
    return (
      /*
        A `<div class="field">` with a bound label rather than a wrapping one, and the cards below
        are built the same way for the same reason: a control nested inside its own `<label>`
        disappears from the accessibility tree this repo snapshots (`apps/semantic-snapshot.ts`
        stops at a role that is named from its content), so a regression that dropped the control
        outright would leave the baseline byte-identical. Bound, the control is its own node with
        its own state.
      */
      <div className="field designations__field">
        <label className="designations__question" htmlFor={SELECT_ID}>
          {DESIGNATION_QUESTION}
        </label>
        <select
          id={SELECT_ID}
          value={shown ?? ""}
          onChange={(changed) => {
            // The empty option is "not answered yet", not a designation. Choosing it back is not
            // offered, so nothing here has to un-choose.
            if (changed.target.value) onChoose(changed.target.value);
          }}
        >
          {/* Nothing is preselected where nothing is in force: the server decides the default, and
              a client that showed one as chosen would be deciding it in the only way a donor can
              see. */}
          <option value="">Choose</option>
          {designations.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <fieldset className="block designations">
      <legend className="designations__question">{DESIGNATION_QUESTION}</legend>
      <div className="designations__options">
        {designations.map((option) => {
          const selected = option.id === shown;
          const inputId = `designation-${option.id}`;
          const nameId = `designation-name-${option.id}`;
          const descriptionId = `designation-description-${option.id}`;
          return (
            <div
              key={option.id}
              className={selected ? "designation designation--selected" : "designation"}
            >
              {/*
                `aria-labelledby` rather than the bound label's own text: the name is the answer
                and the description is context for it, and a name taken from the whole card would
                announce the description twice — once as the option, once as its description.
              */}
              <input
                type="radio"
                className="visually-hidden"
                id={inputId}
                name="designation"
                value={option.id}
                checked={selected}
                aria-labelledby={nameId}
                aria-describedby={option.description ? descriptionId : undefined}
                onChange={() => onChoose(option.id)}
              />
              {/* The label fills the card, so the whole card is the target rather than the words. */}
              <label className="designation__body" htmlFor={inputId}>
                <span className="designation__name" id={nameId} dir="auto">
                  {option.name}
                </span>
                {option.description && (
                  <span className="designation__description" id={descriptionId} dir="auto">
                    {option.description}
                  </span>
                )}
              </label>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
